import React, { useState, useEffect } from 'react';
import { X, Scan, AlertCircle, Crown } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import UsageTrackingService from '../services/usageTrackingService';

interface ARScanLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartScan: () => void;
}

export default function ARScanLimitModal({ isOpen, onClose, onStartScan }: ARScanLimitModalProps) {
  const { isStandardPlan, planType } = useSubscription();
  const [usageInfo, setUsageInfo] = useState<{
    used: number;
    limit: number | null;
    remaining: number;
    allowed: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUsageInfo = async () => {
      if (!isOpen) return;
      
      setIsLoading(true);
      try {
        const usageService = UsageTrackingService.getInstance();
        const info = await usageService.canUseFeature('ar_scan');
        const used = await usageService.getUsageCount('ar_scan');
        
        setUsageInfo({
          used,
          limit: info.limit,
          remaining: info.remaining,
          allowed: info.allowed
        });
      } catch (error) {
        console.error('[ARScanLimitModal] Failed to load usage info:', error);
        // Default to allowing scan if there's an error
        setUsageInfo({
          used: 0,
          limit: null,
          remaining: 0,
          allowed: true
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadUsageInfo();
  }, [isOpen]);

  const handleStartScan = () => {
    if (usageInfo?.allowed) {
      onStartScan();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-slate-800/70 to-slate-900/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl max-w-md w-full border border-white/20">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-2xl">
          <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 p-6">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                  <Scan className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">AR Room Scanner</h2>
                  <p className="text-rose-100 text-sm">Scan usage information</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-2 transition-all duration-200"
              >
                <X size={20} />
              </button>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-rose-400/20 to-pink-400/20 backdrop-blur-3xl"></div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 bg-gradient-to-b from-gray-50/50 to-white">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rose-600"></div>
            </div>
          ) : (
            <>
              {/* Usage Stats */}
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 border-2 border-rose-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-white rounded-lg shadow-sm">
                    <Scan className="w-5 h-5 text-rose-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-rose-900 mb-2">Your Scan Usage</h3>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-rose-700">Scans Used:</span>
                        <span className="font-bold text-rose-900">{usageInfo?.used || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-rose-700">Monthly Limit:</span>
                        <span className="font-bold text-rose-900">
                          {usageInfo?.limit === null ? 'Unlimited' : usageInfo?.limit}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-rose-700">Remaining:</span>
                        <span className={`font-bold ${
                          usageInfo?.remaining === 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {usageInfo?.limit === null ? 'Unlimited' : usageInfo?.remaining}
                        </span>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    {usageInfo?.limit !== null && (
                      <div className="mt-3">
                        <div className="w-full bg-rose-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-rose-500 to-pink-500 rounded-full transition-all duration-300"
                            style={{ 
                              width: `${Math.min(((usageInfo?.used || 0) / (usageInfo?.limit || 1)) * 100, 100)}%` 
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Limit Warning */}
              {!usageInfo?.allowed && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                    <div>
                      <h3 className="text-sm font-semibold text-red-900 mb-1">Scan Limit Reached</h3>
                      <p className="text-xs text-red-700">
                        You've reached your monthly scan limit. Upgrade to Pro for unlimited AR room scans.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Plan Info */}
              {isStandardPlan && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <Crown className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-blue-900 mb-1">Upgrade for More Scans</h3>
                      <p className="text-xs text-blue-700 mb-2">
                        Pro plan includes unlimited AR room scans plus many other features.
                      </p>
                      <a
                        href="https://www.noorcad.com/account"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 underline"
                      >
                        Upgrade to Pro →
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleStartScan}
                  disabled={!usageInfo?.allowed}
                  className={`flex-1 px-4 py-3 font-medium rounded-xl transition-all duration-200 flex items-center justify-center gap-2 ${
                    usageInfo?.allowed
                      ? 'bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <Scan className="w-4 h-4" />
                  Start Scan
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
